import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = "https://365luxuryhomes.com/api/home-content";
const getToken = () => localStorage.getItem("authToken");
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// ─── Reusable Image Upload Component ──────────────────────
const ImageUpload = ({ value, onChange, onUpload, label, aspect = "video", uploading }) => {
  const fileRef = useRef(null);
  const aspectClass = aspect === "square" ? "w-20 h-20" : aspect === "icon" ? "w-14 h-14" : "w-full h-32";
  const previewClass = aspect === "square" ? "w-20 h-20" : aspect === "icon" ? "w-14 h-14 p-1" : "w-full h-32";
  const objectFit = aspect === "icon" ? "object-contain" : "object-cover";

  return (
    <div className="form-control">
      {label && <label className="label py-1"><span className="label-text text-xs font-medium">{label}</span></label>}
      <div
        className={`relative ${aspectClass} border-2 border-dashed border-base-300 rounded-lg overflow-hidden cursor-pointer hover:border-primary/50 transition-colors bg-base-200/50 group`}
        onClick={() => fileRef.current?.click()}
      >
        {value ? (
          <>
            <img src={value} alt="" className={`${previewClass} ${objectFit} rounded-lg`} />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-xs font-medium">
                {uploading ? "Uploading..." : "Change"}
              </span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={20} height={20} fill="none" className="text-base-content/30">
              <path d="M12 4.5V14.5M12 4.5C11.2998 4.5 9.99153 6.4943 9.5 7M12 4.5C12.7002 4.5 14.0085 6.4943 14.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M20 16.5C20 18.982 19.482 19.5 17 19.5H7C4.518 19.5 4 18.982 4 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[10px] text-base-content/30">Upload</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
        />
      </div>
      {value && (
        <button
          type="button"
          className="text-[10px] text-error mt-1 hover:underline self-start"
          onClick={(e) => { e.stopPropagation(); onChange(""); }}
        >
          Remove image
        </button>
      )}
    </div>
  );
};

// ─── Areas Tab ─────────────────────────────────────────────
const AreasTab = () => {
  const [data, setData] = useState({ title: "Discover Best", subtitle: "Areas in Dubai", items: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/areas`);
      if (res.data) setData((prev) => ({ ...prev, ...res.data }));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/upload-image`, fd, { headers: authHeaders() });
      return res.data.url;
    } catch (e) { alert("Image upload failed"); return null; }
    finally { setUploading(false); }
  };

  const handleItemChange = (idx, field, value) => {
    setData((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const handleItemImage = async (idx, file, field) => {
    const url = await uploadImage(file);
    if (url) handleItemChange(idx, field, url);
  };

  const addItem = () => {
    setData((prev) => ({ ...prev, items: [...prev.items, { name: "", image: "", icon: "", url: "/short-term-rentals/" }] }));
  };

  const removeItem = (idx) => {
    setData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/areas`, data, { headers: authHeaders() });
      alert("Areas saved!");
    } catch (e) { alert("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg"></span></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">Section Title</span></label>
          <input className="input input-bordered" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
        </div>
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">Section Subtitle</span></label>
          <input className="input input-bordered" value={data.subtitle} onChange={(e) => setData({ ...data, subtitle: e.target.value })} />
        </div>
      </div>

      <div className="divider">Area Items</div>

      {data.items.map((item, idx) => (
        <div key={idx} className="card bg-base-100 shadow-sm border border-base-300">
          <div className="card-body p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-sm">Area #{idx + 1}{item.name ? `: ${item.name}` : ""}</h4>
              <button className="btn btn-ghost btn-xs text-error" onClick={() => removeItem(idx)}>Remove</button>
            </div>
            <div className="flex gap-4">
              <div className="flex gap-3 shrink-0">
                <ImageUpload
                  value={item.image}
                  label="Image"
                  aspect="square"
                  uploading={uploading}
                  onChange={(v) => handleItemChange(idx, "image", v)}
                  onUpload={(f) => handleItemImage(idx, f, "image")}
                />
                <ImageUpload
                  value={item.icon}
                  label="Icon"
                  aspect="icon"
                  uploading={uploading}
                  onChange={(v) => handleItemChange(idx, "icon", v)}
                  onUpload={(f) => handleItemImage(idx, f, "icon")}
                />
              </div>
              <div className="flex-1 space-y-3">
                <div className="form-control">
                  <label className="label py-1"><span className="label-text text-xs font-medium">Name</span></label>
                  <input className="input input-bordered input-sm" value={item.name} onChange={(e) => handleItemChange(idx, "name", e.target.value)} />
                </div>
                <div className="form-control">
                  <label className="label py-1"><span className="label-text text-xs font-medium">URL Path</span></label>
                  <input className="input input-bordered input-sm" value={item.url} onChange={(e) => handleItemChange(idx, "url", e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="flex gap-3">
        <button className="btn btn-outline btn-sm" onClick={addItem}>+ Add Area</button>
        <button className={`btn btn-primary btn-sm ${saving ? "loading" : ""}`} onClick={save} disabled={saving || uploading}>
          {saving ? "Saving..." : "Save Areas"}
        </button>
      </div>
    </div>
  );
};

// ─── About Tab ─────────────────────────────────────────────
const AboutTab = () => {
  const [data, setData] = useState({
    title: "About Us",
    description: "",
    image: "",
    stats: [{ value: 160, suffix: "+", label: "Active Listings" }, { value: 140, suffix: "M", label: "In Sales" }, { value: 500, suffix: "+", label: "Happy Clients" }],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/about`);
        if (res.data) setData((prev) => ({ ...prev, ...res.data }));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/upload-image`, fd, { headers: authHeaders() });
      return res.data.url;
    } catch (e) { alert("Image upload failed"); return null; }
    finally { setUploading(false); }
  };

  const handleStatChange = (idx, field, value) => {
    setData((prev) => {
      const stats = [...prev.stats];
      stats[idx] = { ...stats[idx], [field]: field === "value" ? Number(value) || 0 : value };
      return { ...prev, stats };
    });
  };

  const addStat = () => setData((prev) => ({ ...prev, stats: [...prev.stats, { value: 0, suffix: "+", label: "" }] }));
  const removeStat = (idx) => setData((prev) => ({ ...prev, stats: prev.stats.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/about`, data, { headers: authHeaders() });
      alert("About section saved!");
    } catch (e) { alert("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg"></span></div>;

  return (
    <div className="space-y-4">
      <div className="form-control">
        <label className="label"><span className="label-text font-medium">Title</span></label>
        <input className="input input-bordered" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
      </div>
      <div className="form-control">
        <label className="label"><span className="label-text font-medium">Description</span></label>
        <textarea className="textarea textarea-bordered h-32" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} />
      </div>
      <ImageUpload
        value={data.image}
        label="Section Image"
        aspect="video"
        uploading={uploading}
        onChange={(v) => setData((p) => ({ ...p, image: v }))}
        onUpload={async (f) => { const url = await uploadImage(f); if (url) setData((p) => ({ ...p, image: url })); }}
      />
      <div className="divider">Stats</div>
      {data.stats.map((stat, idx) => (
        <div key={idx} className="flex items-end gap-3">
          <div className="form-control flex-1">
            <label className="label py-1"><span className="label-text text-xs">Value</span></label>
            <input className="input input-bordered input-sm" type="number" value={stat.value} onChange={(e) => handleStatChange(idx, "value", e.target.value)} />
          </div>
          <div className="form-control w-20">
            <label className="label py-1"><span className="label-text text-xs">Suffix</span></label>
            <input className="input input-bordered input-sm" value={stat.suffix} onChange={(e) => handleStatChange(idx, "suffix", e.target.value)} />
          </div>
          <div className="form-control flex-1">
            <label className="label py-1"><span className="label-text text-xs">Label</span></label>
            <input className="input input-bordered input-sm" value={stat.label} onChange={(e) => handleStatChange(idx, "label", e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm text-error" onClick={() => removeStat(idx)}>X</button>
        </div>
      ))}
      <div className="flex gap-3">
        <button className="btn btn-outline btn-sm" onClick={addStat}>+ Add Stat</button>
        <button className={`btn btn-primary btn-sm ${saving ? "loading" : ""}`} onClick={save} disabled={saving || uploading}>
          {saving ? "Saving..." : "Save About"}
        </button>
      </div>
    </div>
  );
};

// ─── ListWithUs Tab ────────────────────────────────────────
const ListWithUsTab = () => {
  const [data, setData] = useState({
    title: "List With Us",
    description: "",
    buttonText: "Enquire Now",
    buttonUrl: "/list-with-us",
    backgroundImage: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/listwithus`);
        if (res.data) setData((prev) => ({ ...prev, ...res.data }));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/upload-image`, fd, { headers: authHeaders() });
      return res.data.url;
    } catch (e) { alert("Image upload failed"); return null; }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/listwithus`, data, { headers: authHeaders() });
      alert("List With Us section saved!");
    } catch (e) { alert("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg"></span></div>;

  return (
    <div className="space-y-4">
      <div className="form-control">
        <label className="label"><span className="label-text font-medium">Title</span></label>
        <input className="input input-bordered" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
      </div>
      <div className="form-control">
        <label className="label"><span className="label-text font-medium">Description</span></label>
        <textarea className="textarea textarea-bordered h-32" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">Button Text</span></label>
          <input className="input input-bordered" value={data.buttonText} onChange={(e) => setData({ ...data, buttonText: e.target.value })} />
        </div>
        <div className="form-control">
          <label className="label"><span className="label-text font-medium">Button URL</span></label>
          <input className="input input-bordered" value={data.buttonUrl} onChange={(e) => setData({ ...data, buttonUrl: e.target.value })} />
        </div>
      </div>
      <ImageUpload
        value={data.backgroundImage}
        label="Background Image"
        aspect="video"
        uploading={uploading}
        onChange={(v) => setData((p) => ({ ...p, backgroundImage: v }))}
        onUpload={async (f) => { const url = await uploadImage(f); if (url) setData((p) => ({ ...p, backgroundImage: url })); }}
      />
      <button className={`btn btn-primary btn-sm ${saving ? "loading" : ""}`} onClick={save} disabled={saving || uploading}>
        {saving ? "Saving..." : "Save List With Us"}
      </button>
    </div>
  );
};

// ─── Featured Tab ──────────────────────────────────────────
const FeaturedTab = () => {
  const [data, setData] = useState({ title: "As Featured In", logos: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/featured`);
        if (res.data) setData((prev) => ({ ...prev, ...res.data }));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/upload-image`, fd, { headers: authHeaders() });
      return res.data.url;
    } catch (e) { alert("Image upload failed"); return null; }
    finally { setUploading(false); }
  };

  const handleLogoChange = (idx, field, value) => {
    setData((prev) => {
      const logos = [...prev.logos];
      logos[idx] = { ...logos[idx], [field]: value };
      return { ...prev, logos };
    });
  };

  const addLogo = () => setData((prev) => ({ ...prev, logos: [...prev.logos, { name: "", image: "", width: "w-40" }] }));
  const removeLogo = (idx) => setData((prev) => ({ ...prev, logos: prev.logos.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/featured`, data, { headers: authHeaders() });
      alert("Featured section saved!");
    } catch (e) { alert("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center p-8"><span className="loading loading-spinner loading-lg"></span></div>;

  return (
    <div className="space-y-4">
      <div className="form-control">
        <label className="label"><span className="label-text font-medium">Section Title</span></label>
        <input className="input input-bordered" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
      </div>
      <div className="divider">Logos</div>
      {data.logos.map((logo, idx) => (
        <div key={idx} className="flex items-center gap-4 p-3 bg-base-200 rounded-lg">
          <ImageUpload
            value={logo.image}
            aspect="icon"
            uploading={uploading}
            onChange={(v) => handleLogoChange(idx, "image", v)}
            onUpload={async (f) => { const url = await uploadImage(f); if (url) handleLogoChange(idx, "image", url); }}
          />
          <div className="form-control flex-1">
            <label className="label py-1"><span className="label-text text-xs font-medium">Name</span></label>
            <input className="input input-bordered input-sm" value={logo.name} onChange={(e) => handleLogoChange(idx, "name", e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm text-error self-end mb-1" onClick={() => removeLogo(idx)}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={16} height={16} fill="none">
              <path d="M19.5 5.5L18.613 15.412C18.3691 18.1074 18.2472 19.4551 17.4465 20.2275C16.6458 21 15.4795 21 13.147 21H10.853C8.52045 21 7.35418 21 6.55352 20.2275C5.75286 19.4551 5.63091 18.1074 5.387 15.412L4.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M3 5.5H21M16.055 5.5L15.17 3.712C14.585 2.543 14.293 1.958 13.773 1.603C13.6072 1.49066 13.4312 1.39403 13.248 1.314C12.678 1.07 12.028 1.07 10.73 1.07C9.395 1.07 8.727 1.07 8.147 1.325C7.96162 1.40876 7.7844 1.509 7.618 1.625C7.091 2 6.81 2.607 6.247 3.822L5.453 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      ))}
      <div className="flex gap-3">
        <button className="btn btn-outline btn-sm" onClick={addLogo}>+ Add Logo</button>
        <button className={`btn btn-primary btn-sm ${saving ? "loading" : ""}`} onClick={save} disabled={saving || uploading}>
          {saving ? "Saving..." : "Save Featured"}
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────
const HomePageManager = () => {
  const [activeTab, setActiveTab] = useState("areas");

  const tabs = [
    { id: "areas", label: "Areas" },
    { id: "about", label: "About" },
    { id: "listwithus", label: "List With Us" },
    { id: "featured", label: "Featured" },
  ];

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-xl font-bold mb-4">Home Page Manager</h1>
      <div role="tablist" className="tabs tabs-bordered mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            className={`tab ${activeTab === tab.id ? "tab-active font-semibold" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="bg-base-100 rounded-lg p-4 lg:p-6 shadow-sm">
        {activeTab === "areas" && <AreasTab />}
        {activeTab === "about" && <AboutTab />}
        {activeTab === "listwithus" && <ListWithUsTab />}
        {activeTab === "featured" && <FeaturedTab />}
      </div>
    </div>
  );
};

export default HomePageManager;
